import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  Users,
  UserPlus,
  Upload,
  Edit,
  Trash2,
  Search,
  Loader2,
  AlertCircle,
  Download,
  CheckCircle,
  RefreshCw,
  Eye,
  EyeOff,
  FileSpreadsheet,
  UserMinus,
  Calendar as CalendarIcon,
} from "lucide-react";

const RoleBadge = ({ role }) => {
  const roleConfig = {
    empleado: { label: "Colaborador", className: "bg-slate-100 text-slate-700" },
    supervisor: { label: "Supervisor", className: "bg-sky-100 text-sky-700" },
    rrhh: { label: "RRHH", className: "bg-purple-100 text-purple-700 border-purple-200 font-medium" },
    administrador: { label: "Admin", className: "bg-orange-100 text-orange-700" },
  };

  const config = roleConfig[role?.toLowerCase()] || roleConfig.empleado;

  return (
    <Badge variant="outline" className={config.className}>
      {config.label}
    </Badge>
  );
};

const AdminPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.rol === "administrador";

  const [employees, setEmployees] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [formError, setFormError] = useState("");

  const [filterDepartment, setFilterDepartment] = useState("all");
  const [filterCompany, setFilterCompany] = useState("all");
  const [filterSupervisor, setFilterSupervisor] = useState("all");

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [targetSupervisorId, setTargetSupervisorId] = useState("");
  
  const [manualAdjustmentDialogOpen, setManualAdjustmentDialogOpen] = useState(false);
  const [selectedEmployeeForAdjustment, setSelectedEmployeeForAdjustment] = useState(null);
  const [manualAdjustmentData, setManualAdjustmentData] = useState({
    dias: 0,
    motivo: "",
  });
  const [reportLoading, setReportLoading] = useState(false);
  const [holidaysList, setHolidaysList] = useState([]);
  const [selectedHolidayCountry, setSelectedHolidayCountry] = useState("Guatemala");
  const [holidayFormOpen, setHolidayFormOpen] = useState(false);
  const [newHolidayData, setNewHolidayData] = useState({ fecha: "", nombre: "", pais: "Guatemala" });
  const [holidayLoading, setHolidayLoading] = useState(false);
  const [selectedHolidayYear, setSelectedHolidayYear] = useState(new Date().getFullYear().toString());
  const [editingHoliday, setEditingHoliday] = useState(null);

  const [formData, setFormData] = useState({
    dpi: "",
    nombre: "",
    apellido: "",
    email: "",
    fecha_nacimiento: "",
    departamento: "",
    puesto: "",
    rol: "empleado",
    supervisor_id: "",
    empresa: "",
    dias_iniciales: 0,
    fecha_ingreso: "",
    activo: true,
    pais: "Guatemala",
    puede_marcajes: false,
    puede_aprobar: false,
  });

  const fetchEmployees = async () => {
    try {
      const [empResponse, supResponse] = await Promise.all([
        api.getEmployees(),
        api.getSupervisors(),
      ]);
      setEmployees(empResponse.data);
      setSupervisors(supResponse.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error al cargar los datos");
    } finally {
      setLoading(false);
    }
  };

  const fetchHolidays = async (pais) => {
    setHolidayLoading(true);
    try {
      const response = await api.getHolidays(pais);
      setHolidaysList(response.data);
    } catch (error) {
      console.error("Error fetching holidays:", error);
      toast.error("Error al cargar los feriados");
    } finally {
      setHolidayLoading(false);
    }
  };

  const handleAddHoliday = async (e) => {
    e.preventDefault();
    if (!newHolidayData.fecha || !newHolidayData.nombre) {
      toast.error("Por favor completa todos los campos");
      return;
    }
    setFormLoading(true);
    try {
      if (editingHoliday) {
        await api.updateHoliday(editingHoliday.id, newHolidayData);
        toast.success("Feriado actualizado correctamente");
      } else {
        await api.createHoliday(newHolidayData);
        toast.success("Feriado agregado correctamente");
      }
      setHolidayFormOpen(false);
      setNewHolidayData({ fecha: "", nombre: "", pais: selectedHolidayCountry });
      setEditingHoliday(null);
      fetchHolidays(selectedHolidayCountry);
    } catch (error) {
      console.error("Error saving holiday:", error);
      toast.error(error.response?.data?.detail || "Error al guardar el feriado");
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteHoliday = async (id, nombre) => {
    if (!window.confirm(`¿Está seguro de eliminar el feriado "${nombre}"?`)) {
      return;
    }
    try {
      await api.deleteHoliday(id);
      toast.success("Feriado eliminado correctamente");
      fetchHolidays(selectedHolidayCountry);
    } catch (error) {
      console.error("Error deleting holiday:", error);
      toast.error("Error al eliminar el feriado");
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    fetchHolidays(selectedHolidayCountry);
  }, [selectedHolidayCountry]);

  const resetForm = () => {
    setFormData({
      dpi: "",
      nombre: "",
      apellido: "",
      email: "",
      fecha_nacimiento: "",
      departamento: "",
      puesto: "",
      rol: "empleado",
      supervisor_id: "",
      empresa: "",
      dias_iniciales: 0,
      fecha_ingreso: "",
      activo: true,
      pais: "Guatemala",
      puede_marcajes: false,
      puede_aprobar: false,
    });
    setFormError("");
    setEditingEmployee(null);
    setSelectedIds(new Set());
  };

  const handleOpenDialog = (employee = null) => {
    if (employee) {
      setEditingEmployee(employee);
      setFormData({
        dpi: employee.dpi,
        nombre: employee.nombre,
        apellido: employee.apellido,
        email: employee.email,
        fecha_nacimiento: employee.fecha_nacimiento,
        departamento: employee.departamento,
        puesto: employee.puesto,
        rol: employee.rol,
        supervisor_id: employee.supervisor_id || "",
        empresa: employee.empresa || "",
        dias_iniciales: employee.dias_iniciales || 0,
        fecha_ingreso: employee.fecha_ingreso,
        activo: employee.activo,
        pais: employee.pais || "Guatemala",
        puede_marcajes: !!employee.puede_marcajes,
        puede_aprobar: !!employee.puede_aprobar,
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    setFormLoading(true);

    try {
      if (editingEmployee) {
        await api.updateEmployee(editingEmployee.id, formData);
        toast.success("Colaborador actualizado correctamente");
      } else {
        await api.createEmployee(formData);
        toast.success("Colaborador creado correctamente");
      }
      setDialogOpen(false);
      fetchEmployees();
    } catch (error) {
      console.error("Error saving employee:", error);
      setFormError(error.response?.data?.detail || "Error al guardar el colaborador");
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (employee) => {
    if (!window.confirm(`¿Está seguro de desactivar a ${employee.nombre} ${employee.apellido}?`)) {
      return;
    }

    try {
      await api.deleteEmployee(employee.id);
      toast.success("Colaborador desactivado correctamente");
      fetchEmployees();
    } catch (error) {
      console.error("Error deleting employee:", error);
      toast.error("Error al desactivar el colaborador");
    }
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImportLoading(true);
    setImportResult(null);

    try {
      const response = await api.importEmployees(file);
      const { importados, errores, total, exito } = response.data;
      
      setImportResult({
        total: total || 0,
        importados: importados || 0,
        errores: errores || [],
        success: !!exito
      });

      if (exito) {
        toast.success(`Importación completada: ${importados} colaboradores importados`);
      } else {
        toast.error("No se pudo importar ningún colaborador");
      }
      
      if (errores?.length > 0) {
        console.error("Import errors:", errores);
        toast.warning(`${errores.length} errores durante la importación`);
      }
      
      fetchEmployees();
    } catch (error) {
      console.error("Error importing file:", error);
      toast.error(error.response?.data?.detail || "Error al importar el archivo");
    } finally {
      setImportLoading(false);
      e.target.value = "";
    }
  };

  const handleRecalculate = async () => {
    setLoading(true);
    try {
      await api.recalculateBalances();
      toast.success("Saldos recalculados correctamente");
      fetchEmployees();
    } catch (error) {
      console.error("Error recalculating balances:", error);
      toast.error("Error al recalcular los saldos");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCSVTemplate = () => {
    const headers = "dpi,nombre,apellido,email,fecha_nacimiento,departamento,puesto,rol,supervisor_id,empresa,dias_iniciales,fecha_ingreso";
    const example = "1234567890101,Juan,Pérez,juan@empresa.com,1990-01-15,Tecnología,Desarrollador,empleado,,Empresa Demo,0,2024-01-01";
    const csvContent = `${headers}\n${example}`;
    
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "plantilla_colaboradores.csv";
    link.click();
  };

  const handleDownloadExcelTemplate = () => {
    handleDownloadCSVTemplate();
    toast.info("Se ha descargado la plantilla en formato CSV, compatible con Excel");
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredEmployees.length && filteredEmployees.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredEmployees.map(emp => emp.id)));
    }
  };

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const handleBulkUpdateSupervisor = async () => {
    if (!targetSupervisorId) {
      toast.error("Seleccione un supervisor");
      return;
    }
    try {
      setFormLoading(true);
      await api.bulkUpdateSupervisor(Array.from(selectedIds), targetSupervisorId);
      toast.success("Supervisores actualizados correctamente");
      setBulkDialogOpen(false);
      setSelectedIds(new Set());
      fetchEmployees();
    } catch (error) {
      console.error(error);
      toast.error("Error al actualizar supervisores");
    } finally {
      setFormLoading(false);
    }
  };

  const handleManualAdjustment = async (e) => {
    e.preventDefault();
    if (!selectedEmployeeForAdjustment) return;
    
    setFormLoading(true);
    try {
      await api.manualAdjustment(selectedEmployeeForAdjustment.id, manualAdjustmentData);
      toast.success("Ajuste manual realizado correctamente");
      setManualAdjustmentDialogOpen(false);
      setManualAdjustmentData({ dias: 0, motivo: "" });
      fetchEmployees();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Error al realizar el ajuste manual");
    } finally {
      setFormLoading(false);
    }
  };

  const handleDownloadReport = async () => {
    setReportLoading(true);
    try {
      const params = {
        departamento: filterDepartment === "all" ? undefined : filterDepartment,
        empresa: filterCompany === "all" ? undefined : filterCompany,
        supervisor_id: filterSupervisor === "all" ? undefined : filterSupervisor,
        activo: showInactive ? undefined : true,
      };
      
      const response = await api.getEmployeeReport(params);
      const data = response.data;
      
      if (!data || data.length === 0) {
        toast.error("No hay datos para exportar con los filtros seleccionados");
        return;
      }
      
      // Convert JSON to CSV
      const headers = ["Nombre", "Apellido", "DPI", "Email", "Empresa", "Departamento", "Puesto", "Jefe Directo", "Días Acumulados", "Días Gozados", "Días Disponibles", "Estado", "Fecha de Baja", "Baja Por"];
      const csvRows = [headers.join(",")];
      
      data.forEach(emp => {
        const row = [
          `"${emp.nombre}"`,
          `"${emp.apellido}"`,
          `"${emp.dpi}"`,
          `"${emp.email}"`,
          `"${emp.empresa || 'N/A'}"`,
          `"${emp.departamento}"`,
          `"${emp.puesto}"`,
          `"${emp.supervisor_nombre || 'N/A'}"`,
          emp.dias_acumulados,
          emp.dias_usados,
          emp.dias_disponibles,
          emp.activo ? "Activo" : "Inactivo",
          `"${emp.fecha_baja || ''}"`,
          `"${emp.baja_por || ''}"`
        ];
        csvRows.push(row.join(","));
      });
      
      const csvString = csvRows.join("\n");
      const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      const dateStr = new Date().toISOString().split('T')[0];
      link.download = `reporte_vacaciones_${dateStr}.csv`;
      link.click();
      
      toast.success("Reporte generado exitosamente");
    } catch (error) {
      console.error(error);
      toast.error("Error al generar el reporte");
    } finally {
      setReportLoading(false);
    }
  };

  const filteredEmployees = employees.filter((emp) => {
    const matchesSearch = `${emp.nombre} ${emp.apellido} ${emp.dpi} ${emp.email}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    
    const matchesDepartment = filterDepartment === "all" || emp.departamento === filterDepartment;
    const matchesCompany = filterCompany === "all" || (emp.empresa || "N/A") === filterCompany;
    const matchesSupervisor = filterSupervisor === "all" || emp.supervisor_id === filterSupervisor;
    
    return matchesSearch && matchesDepartment && matchesCompany && matchesSupervisor;
  });

  const uniqueDepartments = Array.from(new Set(employees.map(e => e.departamento))).filter(Boolean).sort();
  const uniqueCompanies = Array.from(new Set(employees.map(e => e.empresa || "N/A"))).filter(Boolean).sort();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="admin-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 tracking-tight">
            Administración
          </h1>
          <p className="text-slate-500 mt-1">
            Gestiona colaboradores y configuración del sistema
          </p>
        </div>
      </div>

      <Tabs defaultValue="empleados" className="space-y-6">
        <TabsList>
          <TabsTrigger value="empleados" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Colaboradores
          </TabsTrigger>
          <TabsTrigger value="inactivos" className="flex items-center gap-2">
            <UserMinus className="w-4 h-4" />
            Inactivos
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="feriados" className="flex items-center gap-2">
              <CalendarIcon className="w-4 h-4" />
              Feriados
            </TabsTrigger>
          )}
          <TabsTrigger value="importar" className="flex items-center gap-2">
            <Upload className="w-4 h-4" />
            Importar CSV
          </TabsTrigger>
          <TabsTrigger value="reportes" className="flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4" />
            Reportes
          </TabsTrigger>
        </TabsList>

        {/* Employees Tab (Active) */}
        <TabsContent value="empleados" className="space-y-4">
          <Card className="border-slate-100">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-4 justify-between">
                <div className="flex flex-col sm:flex-row gap-4 flex-1">
                  <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Buscar por nombre, DPI o email..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                      data-testid="search-employees"
                    />
                  </div>
                  
                  <div className="flex gap-2 flex-wrap sm:flex-nowrap">
                    <Select value={filterDepartment} onValueChange={setFilterDepartment}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Departamento" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos los Deptos.</SelectItem>
                        {uniqueDepartments.map(dept => (
                          <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select value={filterCompany} onValueChange={setFilterCompany}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Empresa" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas las Empresas</SelectItem>
                        {uniqueCompanies.map(comp => (
                          <SelectItem key={comp} value={comp}>{comp}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select value={filterSupervisor} onValueChange={setFilterSupervisor}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Supervisor" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos los Superv.</SelectItem>
                        {supervisors.map(sup => (
                          <SelectItem key={sup.id} value={sup.id}>{sup.nombre} {sup.apellido}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleDownloadReport}
                      className="border-slate-200"
                      title="Descargar reporte filtrado"
                    >
                      <Download className="w-4 h-4 text-slate-600" />
                    </Button>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={handleRecalculate}
                    className="border-slate-200 hover:bg-slate-50"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Recalcular Saldos
                  </Button>
                  <Button
                    onClick={() => handleOpenDialog()}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                    data-testid="add-employee-btn"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Nuevo Colaborador
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-100">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-[50px]">
                      <input
                        type="checkbox"
                        className="rounded border-slate-300"
                        checked={selectedIds.size === filteredEmployees.length && filteredEmployees.length > 0}
                        onChange={toggleSelectAll}
                      />
                    </TableHead>
                    <TableHead>Colaborador</TableHead>
                    <TableHead>DPI</TableHead>
                    <TableHead>Departamento</TableHead>
                    <TableHead>Supervisor</TableHead>
                    <TableHead>Rol</TableHead>
                    <TableHead>Días Disponibles</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEmployees.filter(e => e.activo).map((emp) => (
                    <TableRow key={emp.id} className={`hover:bg-slate-50 ${selectedIds.has(emp.id) ? 'bg-orange-50/50' : ''}`}>
                      <TableCell>
                        <input
                          type="checkbox"
                          className="rounded border-slate-300"
                          checked={selectedIds.has(emp.id)}
                          onChange={() => toggleSelect(emp.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <p className="font-medium text-slate-800">
                            {emp.nombre} {emp.apellido}
                          </p>
                          <p className="text-xs text-slate-500">{emp.email}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600 font-mono text-sm">
                        {emp.dpi}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-slate-800">{emp.departamento}</p>
                          <p className="text-xs text-slate-500">{emp.puesto}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <p className="text-slate-800 text-sm">
                            {supervisors.find(s => s.id === emp.supervisor_id)?.nombre || "N/A"}
                          </p>
                          <p className="text-xs text-slate-500">
                            {supervisors.find(s => s.id === emp.supervisor_id)?.apellido || ""}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <RoleBadge role={emp.rol} />
                      </TableCell>
                      <TableCell>
                        <span className="font-semibold text-slate-800">
                          {emp.dias_disponibles || 0}
                        </span>
                        <span className="text-slate-500 text-sm"> días</span>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={
                            emp.activo
                              ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                              : "bg-slate-100 text-slate-500"
                          }
                        >
                          {emp.activo ? "Activo" : "Inactivo"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {emp.activo && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                              onClick={() => {
                                setSelectedEmployeeForAdjustment(emp);
                                setManualAdjustmentDialogOpen(true);
                              }}
                              title="Deducción Manual"
                            >
                              <UserMinus className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-sky-600 hover:text-sky-700 hover:bg-sky-50"
                            onClick={() => window.location.href = `/empleados/${emp.id}`}
                            title="Ver Detalle"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDialog(emp)}
                            data-testid={`edit-emp-${emp.id}`}
                            title="Editar"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDelete(emp)}
                            data-testid={`delete-emp-${emp.id}`}
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Bulk Actions Floating Bar */}
          {selectedIds.size > 0 && (
            <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-8 duration-300">
              <Card className="shadow-2xl border-orange-200 bg-white/90 backdrop-blur-md px-6 py-4 flex items-center gap-6">
                <div className="flex items-center gap-3 border-r border-slate-200 pr-6">
                  <Badge className="bg-orange-500 text-white h-7 w-7 rounded-full flex items-center justify-center p-0">
                    {selectedIds.size}
                  </Badge>
                  <span className="text-sm font-semibold text-slate-700">Seleccionados</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    className="bg-sky-600 hover:bg-sky-700 text-white"
                    onClick={() => setBulkDialogOpen(true)}
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Asignar Supervisor
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-slate-500 hover:text-slate-700"
                    onClick={() => setSelectedIds(new Set())}
                  >
                    Cancelar
                  </Button>
                </div>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Inactivos Tab */}
        <TabsContent value="inactivos" className="space-y-4">
          <Card className="border-slate-100">
            <CardHeader>
              <CardTitle>Histórico de Colaboradores Inactivos</CardTitle>
              <CardDescription>
                Colaboradores que han sido dados de baja del sistema
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>Colaborador</TableHead>
                    <TableHead>DPI</TableHead>
                    <TableHead>Departamento</TableHead>
                    <TableHead>Fecha Baja</TableHead>
                    <TableHead>Baja Por</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEmployees.filter(e => !e.activo).map((emp) => (
                    <TableRow key={emp.id} className="hover:bg-slate-50">
                      <TableCell>
                        <div className="flex flex-col">
                          <p className="font-medium text-slate-800">
                            {emp.nombre} {emp.apellido}
                          </p>
                          <p className="text-xs text-slate-500">{emp.email}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600 font-mono text-sm">
                        {emp.dpi}
                      </TableCell>
                      <TableCell>
                        <p className="text-slate-800">{emp.departamento}</p>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-100">
                          {emp.fecha_baja || emp.fecha_eliminacion || "N/A"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-600">{emp.baja_por || "Sistema"}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-sky-600 hover:text-sky-700 hover:bg-sky-50"
                          onClick={() => window.location.href = `/empleados/${emp.id}`}
                          title="Ver Detalle"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDialog(emp)}
                          title="Reactivar / Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredEmployees.filter(e => !e.activo).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-10 text-slate-500">
                        No hay colaboradores inactivos para mostrar
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Feriados Tab (Solo Administrador) */}
        {isAdmin && (
          <TabsContent value="feriados" className="space-y-4">
          <Card className="border-slate-100 shadow-sm">
            <CardHeader>
              <CardTitle className="text-xl">Gestión de Feriados y Asuetos</CardTitle>
              <CardDescription>Administra los días no laborables por país y año</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between border-b pb-4">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="filtro-pais-feriado" className="shrink-0 font-medium text-sm text-slate-600">País:</Label>
                    <Select
                      value={selectedHolidayCountry}
                      onValueChange={setSelectedHolidayCountry}
                    >
                      <SelectTrigger id="filtro-pais-feriado" className="w-[160px]">
                        <SelectValue placeholder="Seleccionar país" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Guatemala">Guatemala</SelectItem>
                        <SelectItem value="El Salvador">El Salvador</SelectItem>
                        <SelectItem value="Honduras">Honduras</SelectItem>
                        <SelectItem value="Nicaragua">Nicaragua</SelectItem>
                        <SelectItem value="Costa Rica">Costa Rica</SelectItem>
                        <SelectItem value="Panamá">Panamá</SelectItem>
                        <SelectItem value="Estados Unidos">Estados Unidos</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center gap-2">
                    <Label htmlFor="filtro-anio-feriado" className="shrink-0 font-medium text-sm text-slate-600">Año:</Label>
                    <Select
                      value={selectedHolidayYear}
                      onValueChange={setSelectedHolidayYear}
                    >
                      <SelectTrigger id="filtro-anio-feriado" className="w-[100px]">
                        <SelectValue placeholder="Año" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="2025">2025</SelectItem>
                        <SelectItem value="2026">2026</SelectItem>
                        <SelectItem value="2027">2027</SelectItem>
                        <SelectItem value="2028">2028</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={async () => {
                      if (window.confirm(`¿Está seguro de precargar los feriados predeterminados de ${selectedHolidayCountry} para el año ${selectedHolidayYear}?`)) {
                        setHolidayLoading(true);
                        try {
                          const res = await api.preloadHolidays(selectedHolidayCountry, parseInt(selectedHolidayYear));
                          toast.success(res.data.message || "Feriados precargados exitosamente");
                          fetchHolidays(selectedHolidayCountry);
                        } catch (error) {
                          console.error(error);
                          toast.error(error.response?.data?.detail || "Error al precargar los feriados");
                        } finally {
                          setHolidayLoading(false);
                        }
                      }
                    }}
                    className="border-slate-200 text-slate-600 hover:bg-slate-50"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Precargar Feriados de {selectedHolidayYear}
                  </Button>
                  <Button
                    onClick={() => {
                      setEditingHoliday(null);
                      setNewHolidayData({ fecha: `${selectedHolidayYear}-01-01`, nombre: "", pais: selectedHolidayCountry });
                      setHolidayFormOpen(true);
                    }}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                  >
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    Agregar Feriado
                  </Button>
                </div>
              </div>

              {holidayLoading ? (
                <div className="flex items-center justify-center h-48">
                  <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Nombre / Descripción</TableHead>
                      <TableHead>País</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {holidaysList
                      .filter((holiday) => holiday.fecha.startsWith(selectedHolidayYear))
                      .map((holiday) => (
                        <TableRow key={holiday.id}>
                          <TableCell className="font-semibold text-slate-800">
                            {new Date(holiday.fecha + "T00:00:00").toLocaleDateString("es-ES", {
                              day: "numeric",
                              month: "long",
                              year: "numeric"
                            })}
                          </TableCell>
                          <TableCell className="text-slate-600">{holiday.nombre}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-100">{holiday.pais}</Badge>
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-slate-600 hover:text-slate-700 hover:bg-slate-50 mr-1"
                              onClick={() => {
                                setEditingHoliday(holiday);
                                setNewHolidayData({ fecha: holiday.fecha, nombre: holiday.nombre, pais: holiday.pais });
                                setHolidayFormOpen(true);
                              }}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteHoliday(holiday.id, holiday.nombre)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    {holidaysList.filter((holiday) => holiday.fecha.startsWith(selectedHolidayYear)).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-12">
                          <div className="flex flex-col items-center justify-center space-y-3 max-w-md mx-auto">
                            <div className="w-12 h-12 rounded-full bg-orange-50 flex items-center justify-center text-orange-500">
                              <CalendarIcon className="w-6 h-6" />
                            </div>
                            <div className="space-y-1">
                              <h4 className="font-semibold text-slate-800">Sin feriados registrados</h4>
                              <p className="text-sm text-slate-500 font-normal">
                                No se encontraron feriados para {selectedHolidayCountry} en el año {selectedHolidayYear}.
                              </p>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={async () => {
                                if (window.confirm(`¿Está seguro de precargar los feriados predeterminados de ${selectedHolidayCountry} para el año ${selectedHolidayYear}?`)) {
                                  setHolidayLoading(true);
                                  try {
                                    const res = await api.preloadHolidays(selectedHolidayCountry, parseInt(selectedHolidayYear));
                                    toast.success(res.data.message || "Feriados precargados exitosamente");
                                    fetchHolidays(selectedHolidayCountry);
                                  } catch (error) {
                                    console.error(error);
                                    toast.error(error.response?.data?.detail || "Error al precargar los feriados");
                                  } finally {
                                    setHolidayLoading(false);
                                  }
                                }
                              }}
                              className="border-orange-200 text-orange-600 hover:bg-orange-50 hover:text-orange-700 mt-2"
                            >
                              <RefreshCw className="w-4 h-4 mr-2" />
                              Precargar feriados de {selectedHolidayYear}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Import Tab */}
        <TabsContent value="importar" className="space-y-4">
          <Card className="border-slate-100">
            <CardHeader>
              <CardTitle>Importar Colaboradores</CardTitle>
              <CardDescription>
                Carga masiva de colaboradores usando un archivo CSV o Excel (.xlsx, .xls)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-10 border-2 border-dashed border-slate-200 rounded-xl text-center bg-slate-50/50 hover:bg-slate-50 hover:border-orange-200 transition-all cursor-pointer group"
                   onClick={() => document.getElementById('file-upload').click()}>
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <Upload className="w-8 h-8 text-orange-600" />
                </div>
                <p className="text-slate-800 font-medium mb-1">
                  Arrastra tu archivo aquí o haz clic para seleccionar
                </p>
                <p className="text-slate-500 text-sm mb-6">
                  Soporta formatos CSV y Microsoft Excel (.xlsx, .xls)
                </p>
                <input
                  type="file"
                  accept=".csv, .xlsx, .xls"
                  onChange={handleImportCSV}
                  className="hidden"
                  id="file-upload"
                />
                <Button
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                  disabled={importLoading}
                >
                  {importLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Procesando archivo...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Seleccionar Archivo
                    </>
                  )}
                </Button>
              </div>

              {importResult && (
                <div className="space-y-4 animate-in fade-in slide-in-from-top-4 duration-500">
                  <div className="bg-white border rounded-lg p-4 shadow-sm">
                    <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                       <RefreshCw className={`w-4 h-4 ${importLoading ? 'animate-spin' : ''}`} />
                      Resultado de la Importación
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                      <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 text-center">
                        <p className="text-xs text-slate-500 uppercase font-bold tracking-wider">Total Filas</p>
                        <p className="text-2xl font-bold text-slate-800">{importResult.total}</p>
                      </div>
                      <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-100 text-center">
                        <p className="text-xs text-emerald-600 uppercase font-bold tracking-wider">Importados</p>
                        <p className="text-2xl font-bold text-emerald-700">{importResult.importados}</p>
                      </div>
                      <div className="p-3 bg-red-50 rounded-lg border border-red-100 text-center">
                        <p className="text-xs text-red-600 uppercase font-bold tracking-wider">Con Error</p>
                        <p className="text-2xl font-bold text-red-700">{importResult.errores?.length || 0}</p>
                      </div>
                    </div>

                    {importResult.errores?.length > 0 && (
                      <div className="space-y-2 mt-4">
                        <p className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 text-red-500" />
                          Log de Errores Encontrados ({importResult.errores.length})
                        </p>
                        <div className="max-h-[300px] overflow-y-auto border rounded-xl bg-red-50/20">
                          <Table>
                            <TableHeader className="bg-slate-100/50 sticky top-0 z-10 backdrop-blur-sm">
                              <TableRow>
                                <TableHead className="w-[80px] font-bold text-slate-900">Fila</TableHead>
                                <TableHead className="w-[180px] font-bold text-slate-900">ID / Referencia</TableHead>
                                <TableHead className="font-bold text-slate-900">Descripción del Error</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {importResult.errores.map((err, i) => (
                                <TableRow key={i} className="hover:bg-red-100/30 transition-colors border-red-100/50">
                                  <TableCell className="font-mono text-xs font-semibold text-slate-600">
                                    {err?.fila || (i + 1)}
                                  </TableCell>
                                  <TableCell className="font-medium text-xs text-slate-700 truncate max-w-[180px]">
                                    {err?.id || 'N/A'}
                                  </TableCell>
                                  <TableCell className="text-red-700 text-xs font-semibold leading-relaxed">
                                    {err?.error || 'Ocurrió un error inesperado al procesar la fila'}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    )}
                    
                    {importResult.success && (!importResult.errores || importResult.errores.length === 0) && (
                      <Alert className="bg-emerald-50 border-emerald-200 mt-4 shadow-sm border-2">
                        <CheckCircle className="h-5 w-5 text-emerald-600" />
                        <AlertDescription className="text-emerald-800 font-bold ml-2">
                          ¡Éxito! Todos los datos fueron importados sin ninguna incidencia.
                        </AlertDescription>
                      </Alert>
                    )}

                    <div className="mt-8 flex justify-center border-t pt-4 border-slate-100">
                      <Button 
                        variant="link" 
                        size="sm" 
                        onClick={() => setImportResult(null)}
                        className="text-slate-400 hover:text-orange-500 font-semibold"
                      >
                        Cerrar reporte y continuar
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-4">
                <Button
                  variant="outline"
                  onClick={handleDownloadCSVTemplate}
                  data-testid="download-template"
                  className="border-slate-200 group"
                >
                  <Download className="w-4 h-4 mr-2 text-slate-500 group-hover:text-orange-500 transition-colors" />
                  Descargar Plantilla CSV
                </Button>
                <Button
                  variant="outline"
                  onClick={handleDownloadExcelTemplate}
                  className="border-slate-200 group"
                >
                  <Download className="w-4 h-4 mr-2 text-slate-500 group-hover:text-orange-500 transition-colors" />
                  Descargar Plantilla Excel
                </Button>
              </div>

              <Alert className="bg-sky-50 border-sky-200">
                <AlertCircle className="h-4 w-4 text-sky-600" />
                <AlertDescription className="text-sky-800 space-y-2">
                  <p><strong>Instrucciones:</strong></p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li>El archivo debe incluir las columnas obligatorias: <strong>dpi, nombre, apellido, email</strong>.</li>
                    <li>Fechas en formato <strong>YYYY-MM-DD</strong>.</li>
                    <li>Roles válidos: <strong>empleado, supervisor, administrador</strong>.</li>
                    <li>Si el DPI ya existe, la fila será ignorada.</li>
                  </ul>
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reportes Tab */}
        <TabsContent value="reportes" className="space-y-4">
          <Card className="border-slate-100">
            <CardHeader>
              <CardTitle>Reportes de Vacaciones</CardTitle>
              <CardDescription>
                Genera y descarga reportes detallados en formato Excel/CSV
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="p-6 border rounded-xl bg-slate-50/30 flex flex-col items-center text-center space-y-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                    <FileSpreadsheet className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800">Reporte General de Saldos</h3>
                    <p className="text-sm text-slate-500 mt-1">
                      Incluye nombres, días gozados, disponibles y jefes directos.
                    </p>
                  </div>
                  <Button 
                    onClick={handleDownloadReport} 
                    disabled={reportLoading}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                  >
                    {reportLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Descargar Reporte
                  </Button>
                </div>

                <div className="md:col-span-2 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-slate-500">Departamento</Label>
                      <Select value={filterDepartment} onValueChange={setFilterDepartment}>
                        <SelectTrigger className="bg-white">
                          <SelectValue placeholder="Departamento" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos los Deptos.</SelectItem>
                          {uniqueDepartments.map(dept => (
                            <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-500">Empresa</Label>
                      <Select value={filterCompany} onValueChange={setFilterCompany}>
                        <SelectTrigger className="bg-white">
                          <SelectValue placeholder="Empresa" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todas las Empresas</SelectItem>
                          {uniqueCompanies.map(comp => (
                            <SelectItem key={comp} value={comp}>{comp}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-500">Supervisor</Label>
                      <Select value={filterSupervisor} onValueChange={setFilterSupervisor}>
                        <SelectTrigger className="bg-white">
                          <SelectValue placeholder="Supervisor" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos los Superv.</SelectItem>
                          {supervisors.map(sup => (
                            <SelectItem key={sup.id} value={sup.id}>{sup.nombre} {sup.apellido}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Alert className="bg-blue-50 border-blue-100">
                    <AlertCircle className="h-4 w-4 text-blue-600" />
                    <AlertDescription className="text-blue-800 text-xs">
                      El reporte incluye a todos los colaboradores (activos e inactivos) que coincidan con los filtros de departamento y empresa.
                    </AlertDescription>
                  </Alert>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Employee Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingEmployee ? "Editar Colaborador" : "Nuevo Colaborador"}
            </DialogTitle>
            <DialogDescription>
              {editingEmployee 
                ? "Modifica los datos del colaborador seleccionado" 
                : "Completa el formulario para agregar un nuevo colaborador al sistema"}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {formError && (
              <Alert variant="destructive" className="bg-red-50 border-red-200">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="dpi">DPI *</Label>
                <Input
                  id="dpi"
                  value={formData.dpi}
                  onChange={(e) => setFormData({ ...formData, dpi: e.target.value })}
                  placeholder="1234567890101"
                  required
                  data-testid="form-dpi"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="empleado@empresa.com"
                  required
                  data-testid="form-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="nombre">Nombre *</Label>
                <Input
                  id="nombre"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  placeholder="Juan"
                  required
                  data-testid="form-nombre"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="apellido">Apellido *</Label>
                <Input
                  id="apellido"
                  value={formData.apellido}
                  onChange={(e) => setFormData({ ...formData, apellido: e.target.value })}
                  placeholder="Pérez"
                  required
                  data-testid="form-apellido"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="fecha_nacimiento">Fecha de Nacimiento *</Label>
                <Input
                  id="fecha_nacimiento"
                  type="date"
                  value={formData.fecha_nacimiento}
                  onChange={(e) => setFormData({ ...formData, fecha_nacimiento: e.target.value })}
                  required
                  data-testid="form-fecha-nacimiento"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="fecha_ingreso">Fecha de Ingreso *</Label>
                <Input
                  id="fecha_ingreso"
                  type="date"
                  value={formData.fecha_ingreso}
                  onChange={(e) => setFormData({ ...formData, fecha_ingreso: e.target.value })}
                  required
                  data-testid="form-fecha-ingreso"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="departamento">Departamento *</Label>
                <Input
                  id="departamento"
                  value={formData.departamento}
                  onChange={(e) => setFormData({ ...formData, departamento: e.target.value })}
                  placeholder="Tecnología"
                  required
                  data-testid="form-departamento"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="puesto">Puesto *</Label>
                <Input
                  id="puesto"
                  value={formData.puesto}
                  onChange={(e) => setFormData({ ...formData, puesto: e.target.value })}
                  placeholder="Desarrollador"
                  required
                  data-testid="form-puesto"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="rol">Rol *</Label>
                <Select
                  value={formData.rol}
                  onValueChange={(value) => setFormData({ ...formData, rol: value })}
                >
                  <SelectTrigger data-testid="form-rol">
                    <SelectValue placeholder="Seleccionar rol" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="empleado">Colaborador</SelectItem>
                    <SelectItem value="supervisor">Supervisor / Jefe</SelectItem>
                    <SelectItem value="rrhh">Recursos Humanos (RRHH)</SelectItem>
                    <SelectItem value="administrador">Administrador</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="empresa">Empresa</Label>
                <Input
                  id="empresa"
                  value={formData.empresa}
                  onChange={(e) => setFormData({ ...formData, empresa: e.target.value })}
                  placeholder="Empresa S.A."
                  data-testid="form-empresa"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="supervisor_id">Supervisor</Label>
                <Select
                  value={formData.supervisor_id || "none"}
                  onValueChange={(value) => setFormData({ ...formData, supervisor_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger data-testid="form-supervisor">
                    <SelectValue placeholder="Seleccionar supervisor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin supervisor</SelectItem>
                    {supervisors.map((sup) => (
                      <SelectItem key={sup.id} value={sup.id}>
                        {sup.nombre} {sup.apellido}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="dias_iniciales">Días Iniciales</Label>
                <Input
                  id="dias_iniciales"
                  type="number"
                  step="0.5"
                  value={formData.dias_iniciales}
                  onChange={(e) => setFormData({ ...formData, dias_iniciales: parseFloat(e.target.value) || 0 })}
                  data-testid="form-dias-iniciales"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="pais">País *</Label>
                <Select
                  value={formData.pais || "Guatemala"}
                  onValueChange={(value) => setFormData({ ...formData, pais: value })}
                >
                  <SelectTrigger data-testid="form-pais">
                    <SelectValue placeholder="Seleccionar país" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Guatemala">Guatemala</SelectItem>
                    <SelectItem value="El Salvador">El Salvador</SelectItem>
                    <SelectItem value="Honduras">Honduras</SelectItem>
                    <SelectItem value="Nicaragua">Nicaragua</SelectItem>
                    <SelectItem value="Costa Rica">Costa Rica</SelectItem>
                    <SelectItem value="Panamá">Panamá</SelectItem>
                    <SelectItem value="Estados Unidos">Estados Unidos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Permisos adicionales, independientes del rol */}
            <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
              <div>
                <Label className="text-slate-800">Permisos adicionales</Label>
                <p className="text-xs text-slate-500 mt-0.5">
                  Dan acceso a estos módulos sin cambiar el rol. Útil para jefes y encargados que son colaboradores.
                </p>
              </div>

              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={!!formData.puede_aprobar}
                  onCheckedChange={(checked) => setFormData({ ...formData, puede_aprobar: !!checked })}
                  data-testid="form-puede-aprobar"
                  className="mt-0.5"
                />
                <span className="text-sm">
                  <span className="font-medium text-slate-800">Aprobar o rechazar vacaciones</span>
                  <span className="block text-xs text-slate-500">
                    Ve el módulo de Aprobaciones con las solicitudes de los colaboradores de su mismo departamento.
                  </span>
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={!!formData.puede_marcajes}
                  onCheckedChange={(checked) => setFormData({ ...formData, puede_marcajes: !!checked })}
                  data-testid="form-puede-marcajes"
                  className="mt-0.5"
                />
                <span className="text-sm">
                  <span className="font-medium text-slate-800">Control de Marcajes y Asistencia</span>
                  <span className="block text-xs text-slate-500">
                    Puede generar y descargar los reportes de marcajes de CrossChex.
                  </span>
                </span>
              </label>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                disabled={formLoading}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="bg-orange-500 hover:bg-orange-600 text-white"
                disabled={formLoading}
                data-testid="form-submit"
              >
                {formLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    {editingEmployee ? "Actualizar" : "Crear"}
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Bulk Update Supervisor Dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Asignar Supervisor Masivo</DialogTitle>
            <DialogDescription>
              Se actualizará el supervisor para {selectedIds.size} colaboradores seleccionados.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>Seleccionar Nuevo Supervisor</Label>
              <Select
                value={targetSupervisorId}
                onValueChange={setTargetSupervisorId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar supervisor" />
                </SelectTrigger>
                <SelectContent>
                  {supervisors.map((sup) => (
                    <SelectItem key={sup.id} value={sup.id}>
                      {sup.nombre} {sup.apellido}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              className="bg-sky-600 hover:bg-sky-700 text-white"
              onClick={handleBulkUpdateSupervisor}
              disabled={formLoading || !targetSupervisorId}
            >
              {formLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Confirmar Cambio
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Manual Adjustment Dialog */}
      <Dialog open={manualAdjustmentDialogOpen} onOpenChange={setManualAdjustmentDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Deducción Manual de Días</DialogTitle>
            <DialogDescription>
              Registra un descuento de días sin pasar por el flujo de aprobación (ej. boletas manuales).
            </DialogDescription>
          </DialogHeader>
          
          {selectedEmployeeForAdjustment && (
            <div className="p-3 bg-orange-50 rounded-lg border border-orange-100 mb-4">
              <p className="text-sm font-medium text-orange-800">
                Colaborador: {selectedEmployeeForAdjustment.nombre} {selectedEmployeeForAdjustment.apellido}
              </p>
              <p className="text-xs text-orange-600">
                Días disponibles actuales: {selectedEmployeeForAdjustment.dias_disponibles}
              </p>
            </div>
          )}

          <form onSubmit={handleManualAdjustment} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="adj_dias">Cantidad de días a descontar *</Label>
              <Input
                id="adj_dias"
                type="number"
                step="0.5"
                min="0.5"
                value={manualAdjustmentData.dias}
                onChange={(e) => setManualAdjustmentData({ ...manualAdjustmentData, dias: parseFloat(e.target.value) })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="adj_motivo">Motivo / Referencia (Boleta #) *</Label>
              <Input
                id="adj_motivo"
                value={manualAdjustmentData.motivo}
                onChange={(e) => setManualAdjustmentData({ ...manualAdjustmentData, motivo: e.target.value })}
                placeholder="Boleta manual #123 - Período anterior"
                required
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setManualAdjustmentDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="bg-orange-600 hover:bg-orange-700 text-white"
                disabled={formLoading}
              >
                {formLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : "Confirmar Deducción"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      {/* Holiday Dialog */}
      <Dialog open={holidayFormOpen} onOpenChange={(open) => {
        setHolidayFormOpen(open);
        if (!open) setEditingHoliday(null);
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingHoliday ? "Editar Feriado" : "Agregar Feriado"}</DialogTitle>
            <DialogDescription>
              {editingHoliday 
                ? "Modifica los datos del día festivo o asueto seleccionado."
                : "Crea un día festivo o asueto. Este día no se podrá seleccionar ni contará para las vacaciones."}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleAddHoliday} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="hol_pais">País *</Label>
              <Select
                value={newHolidayData.pais}
                onValueChange={(value) => setNewHolidayData({ ...newHolidayData, pais: value })}
              >
                <SelectTrigger id="hol_pais">
                  <SelectValue placeholder="Seleccionar país" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Guatemala">Guatemala</SelectItem>
                  <SelectItem value="El Salvador">El Salvador</SelectItem>
                  <SelectItem value="Honduras">Honduras</SelectItem>
                  <SelectItem value="Nicaragua">Nicaragua</SelectItem>
                  <SelectItem value="Costa Rica">Costa Rica</SelectItem>
                  <SelectItem value="Panamá">Panamá</SelectItem>
                  <SelectItem value="Estados Unidos">Estados Unidos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="hol_fecha">Fecha *</Label>
              <Input
                id="hol_fecha"
                type="date"
                value={newHolidayData.fecha}
                onChange={(e) => setNewHolidayData({ ...newHolidayData, fecha: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="hol_nombre">Descripción / Nombre *</Label>
              <Input
                id="hol_nombre"
                value={newHolidayData.nombre}
                onChange={(e) => setNewHolidayData({ ...newHolidayData, nombre: e.target.value })}
                placeholder="Día de la Independencia"
                required
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setHolidayFormOpen(false)}
                disabled={formLoading}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="bg-orange-500 hover:bg-orange-600 text-white"
                disabled={formLoading}
              >
                {formLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : "Guardar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
